'use strict';

const axios = require('axios');
const moment = require('moment');
const Util = require('../util');

const CNS_ID_CADSUS = 'urn:oid:2.16.840.1.113883.13.236';
const CPF_ID_CADSUS = 'urn:oid:2.16.840.1.113883.13.237';
const CONTATO_CELULAR = 'phone';
const CONTATO_TELEFONICO = 'other';
const CONTATO_EMAIL = 'email';
const ESPECIFICACAO_NUMERO = 'http://hl7.org/fhir/StructureDefinition/iso21090-ADXP-houseNumber';
const ESPECIFICACAO_BAIRRO = 'http://hl7.org/fhir/StructureDefinition/iso21090-ADXP-additionalLocator';
const ESPECIFICACAO_RUA = 'http://hl7.org/fhir/StructureDefinition/iso21090-ADXP-streetName';
const ESPECIFICACAO_COMPLEMENTO = 'http://hl7.org/fhir/StructureDefinition/iso21090-ADXP-additionalLocator';

const RACA_COR = {
    '01': 'Branca',
    '02': 'Preta',
    '03': 'Parda',
    '04': 'Amarela',
    '05': 'Indígena',
    99: 'Ignorada'
};

const SEXO_SIGLA = {
    male: 'M', // Masculino
    female: 'F', // Feminino
    other: 'O', // Outro
    unknown: 'I' // Ignorado
};

function transformRace(value) {
    if (!value) return null;
    return RACA_COR[value];
}

function transformGender(value) {
    return SEXO_SIGLA[value];
}

function transformBirthDate(value) {
    if (!value) return null;
    const date = moment(value, 'YYYY-MM-DD');
    return date.isValid() ? date.toDate() : null;
}

/**
 * Permite tipos date ou string no formato YYYY-MM-DD ou DD/MM/YYYY, e normaliza para o formato YYYY-MM-DD exigido pelo cadsus.
 *
 * @param {*} dataNascimento
 * @returns
 */
function normalizarDataNascimento(dataNascimento) {
    if (!dataNascimento) return null;

    let date;

    if (dataNascimento instanceof Date) {
        date = moment(dataNascimento);
    } else if (typeof dataNascimento === 'string') {
        date = moment(dataNascimento, ['YYYY-MM-DD', 'DD/MM/YYYY']);
    } else {
        return null;
    }

    return date.isValid() ? date.format('YYYY-MM-DD') : null;
}

function _normalizarNome(nome) {
    if (!nome) return null;
    if (typeof nome !== 'string') return null;
    const tokens = nome.split(' ');
    if (tokens.length < 2) return null; // O nome deve conter pelo menos nome e sobrenome

    return Util.util.normalizarNome(nome).toUpperCase();
}

/**
 * No cadsus o nome deve estar tudo em maiúsculo e sem acentos.
 * E a data de nascimento deve ser no formato YYYY-MM-DD.
 *
 * @param {*} param0
 * @returns
 */
const _buscarNoCadsusComplementar = async ({ nome, dataNascimento }, config) => {
    const name = _normalizarNome(nome);
    const birthdate = normalizarDataNascimento(dataNascimento);

    if (!name || !birthdate) {
        return false;
    }

    const searchParams = new URLSearchParams({ name, birthdate });
    const token = await Util.util.gerarTokenApiCentralizada(config);

    try {
        const url = `${config.url_base_cadsus}/r4/Patient?${searchParams.toString()}`;
        console.log(`Busca complementar no CADSUS: ${url}`);
        const response = await axios.get(url, {
            headers: {
                authorization: token
            }
        });
        return response.data;
    } catch (err) {
        return false;
    }
};

const _buscarNoCadsus = async ({ cpf, cns }, config) => {
    const token = await Util.util.gerarTokenApiCentralizada(config);
    const id = cpf || cns;
    const options = {
        json: true,
        headers: {
            authorization: token
        }
    };

    try {
        console.log(`Busca no CADSUS: ${config.url_base_cadsus}/r4/Patient/${id}`);
        const response = await axios.get(`${config.url_base_cadsus}/r4/Patient/${id}`, {
            headers: options.headers
        });
        return response.data;
    } catch (err) {
        return false;
    }
};

const _transformarRespostaCadsus = (payload) => {
    const paciente = {
        nome: Util.extractFhir.extractValueByArray(payload, 'name', 'use', 'official', 'given.0'),
        razaoSocial: Util.extractFhir.extractValueByArray(payload, 'name', 'use', 'usual', 'given.0'),
        sexoSigla: transformGender(Util.extractFhir.extractValue(payload, 'gender')),
        raca: transformRace(Util.extractFhir.extractValueByArray(payload, 'extension', 'url', 'raceCodeDatasus', 'valueCode')),
        dataNascimento: transformBirthDate(Util.extractFhir.extractValue(payload, 'birthDate')),
        cpf: Util.extractFhir.extractValueByArray(payload, 'identifier', 'system', CPF_ID_CADSUS, 'value'),
        cns: Util.extractFhir.extractValueByArray(payload, 'identifier', 'system', CNS_ID_CADSUS, 'value'),
        nomePai: Util.extractFhir.extractValueByArray(payload, 'contact', 'relationship.0.coding.0.code', 'NPRN', 'name.given.0'),
        nomeMae: Util.extractFhir.extractValueByArray(payload, 'contact', 'relationship.0.coding.0.code', 'PRN', 'name.given.0'),
        telefone: Util.extractFhir.extractValueByArray(payload, 'telecom', 'system', CONTATO_TELEFONICO, 'value'),
        celular: Util.extractFhir.extractValueByArray(payload, 'telecom', 'system', CONTATO_CELULAR, 'value'),
        email: Util.extractFhir.extractValueByArray(payload, 'telecom', 'system', CONTATO_EMAIL, 'value'),
        endereco: {
            cep: Util.extractFhir.extractValue(payload, 'address.0.postalCode'),
            logradouro: Util.extractFhir.extractValueByArray(payload, 'address.0.extension', 'url', ESPECIFICACAO_RUA, 'valueString'),
            numero: Util.extractFhir.extractValueByArray(payload, 'address.0.extension', 'url', ESPECIFICACAO_NUMERO, 'valueString'),
            bairro: Util.extractFhir.extractValueByArray(payload, 'address.0.extension', 'url', ESPECIFICACAO_BAIRRO, 'valueString'),
            codigoIbge: Util.extractFhir.extractValue(payload, 'address.0.city'), // Na documentação, o campo city é usado para o código IBGE
            estado: Util.extractFhir.extractValue(payload, 'address.0.state'),
            complemento: Util.extractFhir.extractValueByArray(payload, 'address.0.extension', 'url', ESPECIFICACAO_COMPLEMENTO, 'valueString'),
            pais: Util.extractFhir.extractValue(payload, 'address.0.country')
        }
    };
    return paciente;
};

const PacienteCadsus = {
    buscar: async ({ cpf, cns }, config) => {
        const payload = await _buscarNoCadsus({ cpf, cns }, config);
        const paciente = _transformarRespostaCadsus(payload);
        return paciente;
    },
    buscarPeloNomeEDtNascimento: async ({ nome, dataNascimento }, config) => {
        const payload = await _buscarNoCadsusComplementar({ nome, dataNascimento }, config);

        // O CADSUS retorna uma lista de pacientes. Apenas nomes iguais serão considerados.
        if (payload && payload.entry && payload.entry.length >= 1) {
            const nomeBusca = Util.util.normalizarNome(nome).toUpperCase();
            
            for (const entry of payload.entry) {
                const paciente = _transformarRespostaCadsus(entry.resource);
                const nomePaciente = Util.util.normalizarNome(paciente.nome).toUpperCase();
                if (nomePaciente === nomeBusca) {
                    return paciente;
                }
            }
        }
        return null;
    }
};

module.exports = PacienteCadsus;
